import * as z from "zod";

export const newItemSchema = z.object({
  taxType: z.string().min(1, { message: "Required" }),
  taxRate: z.number(),
  taxAmount: z
    .number({ invalid_type_error: "" })
    .nonnegative({ message: "Only positive numbers allowed" }),
  classification: z.string().min(1, "Required"),
  itemCode: z.string().min(1, "Required"),
  unitPrice: z
    .number({ invalid_type_error: "Required" })
    .nonnegative({ message: "Only positive numbers allowed" }),
  discountRate: z
    .number()
    .nonnegative({ message: "Rate must be equal or more than 0" })
    .default(0),
  feeChargeRate: z
    .number()
    .nonnegative({ message: "Rate must be equal or more than 0" })
    .default(0),
  description: z
    .string()
    .max(300, { message: "Exceed 300 characters" })
    .optional(),
  countryOfOrigin: z.string().length(3, { message: "Required" }),
  isEditable: z.number().nonnegative().lte(1),
  measurement: z.string().nullish(),
  // Inventory Management Fields
  quantity: z
    .number()
    .nonnegative({ message: "Quantity must be a positive number" })
    .default(0),
  weightedAverage: z
    .number()
    .nonnegative({ message: "Weighted average must be a positive number" })
    .optional()
    .nullable(),
  salesGlCode: z.string().optional().nullable().or(z.literal("")),
  purchaseGlCode: z.string().optional().nullable().or(z.literal("")),
  stockType: z.enum(["stock", "non-stock"]).default("stock"),
});

export const invoiceItemSchema = z.object({
  id: z.string().optional(),
  classification: z.string(),
  description: z.string().max(300),
  unitPrice: z.number(),
  taxType: z.enum(["01", "02", "03", "04", "05", "06", "E"]),
  codeNumber: z.string(),
  taxRate: z.number(),
  taxAmount: z.number(),
  detailsOfTaxExemption: z.string().nullish().default(null), // Mandatory if tax exemption is applicable
  amountExemptedFromTax: z.number().nullish().default(null), // Mandatory if detailsOfTaxExemption is provided
  subtotal: z.number(),
  totalExcludingTax: z.number(),
  quantity: z.number(),
  measurement: z.string().max(3).nullish(),
  discountRate: z.number().nullable(),
  discountAmount: z.number().nullable(),
  feeRate: z
    .union([z.number().min(0), z.null()])
    .optional()
    .default(0),
  feeAmount: z.number().nullable(),
  productTariffCode: z.string().max(12).optional().nullable(),
  countryOfOrigin: z.string().max(3).optional(),
  total: z.number(),
  _delete: z.boolean().optional(),
  // Stock tracking fields
  stockType: z.enum(["stock", "non-stock"]).optional(),
  availableQuantity: z.number().optional(),
  // Additional fields for tracking
  sourceInvoiceId: z.string().optional(),
  isEditable: z.number().optional(),
});

export const invoiceSchema = z.object({
  buyerId: z.number(),
  items: z.array(invoiceItemSchema).min(1, "At least one item is required"),
  notes: z.string().optional(),
});

export const companyInfoSchema = z.object({
  name: z.string().nonempty("Company name is required"),
  tin: z.string().nonempty("TIN is required"),
  registrationNumber: z.string().nonempty("Registration number is required"),
  sstRegistrationNumber: z.string().optional(),
  email: z.string().email("Invalid email address"),
  contactNumber: z.string().nonempty("Contact number is required"),
  msicCode: z.string().optional(),
  businessActivityDescription: z.string().optional(),
  baseCurrency: z.string().nonempty("Base currency is required"),
  address: z.object({
    addressLine0: z.string().nonempty("Address Line 0 is required"),
    addressLine1: z.string().optional(),
    cityName: z.string().nonempty("City name is required"),
    state: z.string().nonempty("State is required"),
    country: z.string().nonempty("Country is required"),
  }),
});

export const addressSchemaWhitOutID = z.object({
  addressLine0: z.string().min(1, "Address is required."),
  addressLine1: z.string().nullable(),
  addressLine2: z.string().nullable(),
  postalZone: z.string().nullable(),
  cityName: z.string(),
  state: z.string().min(1, "State is required."),
  country: z.string(),
});

export const addressSchema = addressSchemaWhitOutID.extend({
  id: z.string(),
});

export const newBuyerSchema = z.object({
  code: z.string().min(1, "Buyer code is required"),
  name: z.string().min(1, "Name is required"),
  tin: z
    .string()
    .min(1, "TIN is required")
    .max(14, { message: "Exceed maximum characters allowed" })
    .regex(/^[A-Z]{1,2}\d{1,12}$/, "Invalid TIN format"),
  contactNumber: z
    .string()
    .regex(/^\d{10,11}$/, "Phone number must be 10-11 digits"),
  email: z.string().email("Invalid email address"),
  buyerAddress: addressSchemaWhitOutID,
  addressId: z.string().uuid().optional(),
  registrationType: z
    .string({ invalid_type_error: "TIN is required" })
    .min(1, "Registration Type is required."),
  registrationNumber: z.string().min(1, "Registration Number is required"),
  sstRegistrationNumber: z.string().nullable(),
});

export const buyerSchema = newBuyerSchema.extend({
  id: z.string(),
  code: z.string().min(1, "Buyer code is required"),
  buyerAddress: addressSchema,
});

// Editing an existing buyer
export const editBuyerSchema = buyerSchema.extend({
  code: z.string().max(300).nullish(),
});

const TYPE_CODE = z.enum(
  [
    "01",
    "02",
    "03",
    "04",
    "11",
    "12",
    "13",
    "14",
    "01A",
    "GI",
    "GO",
    "QT",
    "SO",
    "DO",
  ],
  { message: "Invoice type is required." },
);
export const invoicePrefixNumberSchema = z.object({
  id: z.string(),
  typeCode: TYPE_CODE,
  prefix: z
    .string()
    .min(1, { message: "Prefix must be at least one character(s)" })
    .max(30, { message: "Prefix must be at most 30 characters" }),
  lastNumber: z
    .string({ message: "Last Number is required." })
    .min(1, { message: "Must at least 1 number" })
    .max(20, { message: "Exceed maximum numbers allowed" }),
  isDefault: z.boolean(),
  preview: z
    .string()
    .max(50, { message: "Must not exceed 50 characters" })
    .optional(),
});

export const newSupplierSchema = z.object({
  code: z.string().nullable(),
  name: z.string().min(1, "Name is required"),
  tin: z
    .string()
    .min(1, "TIN is required")
    .max(14, { message: "Exceed maximum characters allowed" })
    .regex(/^[A-Z]{1,2}\d{1,12}$/, "Invalid TIN format"),
  contactNumber: z
    .string()
    .regex(/^\d{10,11}$/, "Phone number must be 10-11 digits"),
  email: z.string().email("Invalid email address"),
  registrationType: z
    .string({ invalid_type_error: "TIN is required" })
    .min(1, "Registration Type is required"),
  tourismTaxRegistrationNumber: z.string().nullable(),
  businessActivityDescription: z.string().nullable(),
  sstRegistrationNumber: z.string().nullable(),
  registrationNumber: z
    .string()
    .min(1, { message: "Registration Number is required" }),
  msicCode: z.string().length(5, { message: "MSIC Code must be 5 characters" }),
  defaultCurrency: z.string().max(3).nullable(),
  address: addressSchemaWhitOutID,
});

export const supplierSchema = newSupplierSchema.extend({
  id: z.string(),
});

export const jobCodeSchema = z.object({
  code: z.string().min(1, "Code is required"),
  name: z.string(),
  description: z.string(),
});

export const newBranchSchema = z.object({
  prefixCode: z.string().min(4, { message: "Minimum 4 characters" }),
  name: z.string().min(4, { message: "Minimum 4 characters" }),
  contactNumber: z
    .string()
    .regex(/^\d{10,11}$/, "Phone number must be 10-11 digits"),
  email: z.string().email("Invalid email"),
  address: addressSchemaWhitOutID,
});

export const deletionSchema = z.object({
  deletionReason: z.string().min(1, { message: "Required" }),
});

export const newUnitOfMeasurementSchema = z.object({
  description: z
    .string()
    .min(1)
    .refine(
      (val) => val.trim().length > 0,
      "Description cannot be empty or spaces only",
    ),
  lhdnCode: z
    .string()
    .min(1)
    .refine(
      (val) => val.trim().length > 0,
      "Description cannot be empty or spaces only",
    ),
});

export const unitOfMeasurementSchema = newUnitOfMeasurementSchema.extend({
  id: z.string().min(1),
});

export type NewBuyerFormData = z.infer<typeof newBuyerSchema>;
export type NewItemFormData = z.infer<typeof newItemSchema>;
export type InvoiceFormData = z.infer<typeof invoiceSchema>;
export type CompanyInfo = z.infer<typeof companyInfoSchema>;
export type buyerFormData = z.infer<typeof buyerSchema>;
export type InvoicePrefixNumberFormData = z.infer<
  typeof invoicePrefixNumberSchema
>;
export type NewSupplierFormData = z.infer<typeof newSupplierSchema>;
export type supplierFormData = z.infer<typeof supplierSchema>;
export type InvoiceTypeCode = z.infer<typeof TYPE_CODE>;
export type JobCodeSchema = z.infer<typeof jobCodeSchema>;
export type LineItemForm = z.infer<typeof invoiceItemSchema>;
export type NewBranchForm = z.infer<typeof newBranchSchema>;
export type DeletionForm = z.infer<typeof deletionSchema>;
